<template>
  <div class="page">
    <div
      class="right-bar-wrapper"
      :class="{ 'in-ws': inWorkspace }"
      v-if="stateCommon.notification.notificationList.length > 0"
    >
      <Notification
        class="slide-enter-active"
        v-for="(notify, idx) in stateCommon.notification.notificationList"
        :key="notify.id"
        :message="notify.message"
        :status="notify.status"
        :title="notify.title"
        @close="removeNotification(idx)"
      ></Notification>
    </div>
    <!-- Inventory is a workspace of its own (owner 2026-11-02): its header and tabs instead of the sidebar and the
         top bar, in the same tab. The drawer, the notifications and everything loaded here stay as they are. -->
    <InventoryWorkspace v-if="inWorkspace">
      <PickStore v-if="needsStore" :page="$route.name" />
      <router-view v-else v-slot="{ Component }">
        <transition name="iw-page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </InventoryWorkspace>
    <template v-else>
      <Sidebar ref="sidebar"></Sidebar>
      <TopBar></TopBar>
      <div class="flex">
        <div class="grow content">
          <!-- "All stores" is picked and this page works one store at a time: ask for a store instead of opening it. -->
          <PickStore v-if="needsStore" :page="$route.name" />
          <router-view v-else />
        </div>
      </div>
    </template>
    <RightDrawer></RightDrawer>
    <BrandMark class="brand-watermark" v-if="isHome" />
    <FooterWrapper v-if="!inWorkspace"></FooterWrapper>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import store from "@/store";
import { rememberedChoice } from "@/store/modules/common/stores";
import { needsOneStore } from "@/storeScope";

// Layout
import Sidebar from "@/components/Dashboard/Sidebar/Sidebar";
import TopBar from "@/components/Dashboard/TopBar";
import RightDrawer from "@/components/Dashboard/RightDrawer";
import FooterWrapper from "@/components/Dashboard/FooterWrapper";
import PickStore from "@/components/Dashboard/PickStore";
import InventoryWorkspace from "@/components/Inventory/InventoryWorkspace.vue";
import { isWorkspaceRoute } from "@/views/Inventory/kit/invNav.js";

// Where "Back to Connect" in the Inventory workspace returns: the last Connect page opened in this tab.
const LAST_CONNECT_PAGE = "connect:last-page";
const rememberConnectPage = (route) => {
  if (!route || !route.name || isWorkspaceRoute(route.name)) return;
  try { sessionStorage.setItem(LAST_CONNECT_PAGE, route.fullPath); } catch (e) { /* private mode: back goes home */ }
};

export default {
  name: "Dashboard",
  // The pages render inside <router-view>, which is a component of its own under Vue 3, so
  // this.$parent no longer reaches the Dashboard from a page. Pages that reload the menus and
  // options after a change inject this instead (Products, after copying a product).
  provide() {
    return { loadProductsData: () => this.loadProductsData() };
  },
  data() {
    // Set once the store list is in and the first store (or "All stores") is chosen; store switches before that
    // are the start-up choice itself and must not load the store's data a second time.
    return { storesReady: false };
  },
  computed: {
    ...mapState({
      stateStores: "stores",
      stateMenus: "menus",
      stateCommon: "common",
      stateAuth: "auth",
    }),
    ...mapGetters(["getFirstStoreHash", "isAllStores"]),
    needsStore() {
      return this.isAllStores && needsOneStore(this.$route.name);
    },
    // "All stores" is for head office (see the rbac getter); the API answers it for them only.
    mayPickAllStores() {
      return this.$store.getters.mayPickAllStores;
    },
    // The brand mark sits behind the landing page only. On a report it would
    // just be something else in the way.
    isHome() {
      return !this.$route.name || this.$route.name === "home";
    },
    // An Inventory page: the workspace frames it.
    inWorkspace() {
      return isWorkspaceRoute(this.$route.name);
    },
  },
  components: { Sidebar, TopBar, RightDrawer, FooterWrapper, PickStore, InventoryWorkspace },
  watch: {
    $route: { immediate: true, handler: rememberConnectPage },
    // On a phone the floating menu and the drawer close when a page is chosen.
    "$route.name"() {
      if (this.$store.state.panel.mobile) {
        this.$store.dispatch("closeMobileMenu");
        this.$store.dispatch("closeDrawer");
      }
    },
    // A store picked in the top bar, also coming back from "All stores" (which has no store of its own, so
    // nothing is loaded for it: the pages that use a store's menus are closed while it is picked).
    "stateStores.current.storeHash"(newHash, oldHash) {
      if (this.storesReady && newHash && newHash !== oldHash) {
        this.reloadStoreData();
      }
    },
    // Someone whose stores were narrowed while "All stores" was picked goes back to a store of their own.
    mayPickAllStores(allowed) {
      if (!allowed && this.isAllStores && this.storesReady) this.pickRememberedStore();
    },
  },
  async created() {
    this.firstConfig();
    // What this person may do; the sidebar and the pages read it from the store.
    store.dispatch("getMe").then(() => { store.dispatch("chatInit"); store.dispatch("assistantStatus"); });
    store.dispatch("watchViewport");
    store.commit("LOADING", { isActiveStatus: true });
  },
  async mounted() {
    await this.getStores();
  },
  methods: {
    parseJwt(token) {
      var base64Url = token.split(".")[1];
      var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      var jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    },
    firstConfig() {
      let getLocalToken = localStorage.getItem("token");
      // decode token
      let expTime = this.parseJwt(getLocalToken).exp;
      let now = new Date().getTime();
      if (now > expTime * 1000) {
        // console.log('expired')
        store.dispatch("logoutService");
        this.$router.push("/login");
        return;
      }
      // console.log('not expired')
      this.$http.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${getLocalToken}`;
    },
    async getStores() {
      const remembered = rememberedChoice();
      try {
        let response = await this.axios.get("get/stores");
        if (response.data.status) {
          this.$store.commit("SET_STORE_LIST", response.data);
          const list = this.stateStores.stores;
          if (!list.length) throw new Error("This account has no stores.");
          // The remembered store may not be one this account covers (another person used this browser, or their
          // stores were narrowed since): fall back to the first store they do have instead of an empty picker.
          const picked = list.find((s) => s.shash === remembered.storeHash) || list[0];
          this.stateMenus.sizes = picked.sizes;
          // "All stores" again when it was the last choice, the brand is still in the list and this person may
          // see it (rbac/me decides; it is already on its way from created()).
          let all = false;
          if (remembered.allBrandHash && list.some((s) => s.bhash === remembered.allBrandHash)) {
            if (!this.$store.state.rbac.me.loaded) await this.$store.dispatch("getMe");
            all = this.mayPickAllStores;
          }
          if (all) {
            await this.$store.dispatch("setAllStores", remembered.allBrandHash);
          } else {
            await this.$store.dispatch("setStoreDetails", picked.shash);
            await this.loadProductsData();
          }
          this.storesReady = true;
        } else {
          throw new Error("Cannot get stores.");
        }
      } catch (error) {
        store.commit("NOTIFICATION_MESSAGE", {
          title: "Error",
          message: error.message || "An error occurred",
          status: -1,
        });
      } finally {
        store.commit("LOADING", { isActiveStatus: false });
      }
    },
    /** Back to the store this browser picked last (or the first in the list). */
    pickRememberedStore() {
      const list = Array.isArray(this.stateStores.stores) ? this.stateStores.stores : [];
      const picked = list.find((s) => s.shash === rememberedChoice().storeHash) || list[0];
      if (picked) this.$store.dispatch("setStoreDetails", picked.shash);
    },
    async loadProductsData() {
      store
        .dispatch("getMenus", this.stateStores.current.storeHash)
        .then(() => {
          store.dispatch("getTabsMenu");
          return store.dispatch(
            "getOptions",
            this.stateStores.current.storeHash
          );
        })
        .then(() => {
          store.dispatch("getTabsOptionItems");
        })
        // .then(() => {
        //   store.dispatch("getUnitList");
        // })
        .catch((error) => {
          store.commit("NOTIFICATION_MESSAGE", {
            title: "Products Load Error",
            message: error.message || "Failed to load products data",
            status: -1,
          });
        });
    },
    async reloadStoreData() {
      store.commit("LOADING", { isActiveStatus: true });
      const current = this.stateStores.stores.find(
        (s) => s.shash === this.stateStores.current.storeHash
      );
      if (current) {
        this.stateMenus.sizes = current.sizes;
      }
      // Reset selected tabs so the first menu gets auto-selected
      this.stateMenus.vSelectedTab = "";
      if (this.$store.state.options) {
        this.$store.state.options.vSelectedTab = "";
      }
      try {
        await this.loadProductsData();
      } catch (error) {
        store.commit("NOTIFICATION_MESSAGE", {
          title: "Store Switch Error",
          message: error.message || "Failed to reload store data",
          status: -1,
        });
      } finally {
        store.commit("LOADING", { isActiveStatus: false });
      }
    },
    removeNotification(index) {
      this.$store.commit("REMOVE_NOTIFICATION", index);
    },
  },
};
</script>

<style lang="css" scoped>
.page {
  background: var(--gradient-page, var(--page-bg, #f1f5f9));
  min-height: 100vh;
}
.right-bar-wrapper {
  position: fixed;
  top: calc(var(--topbar-height) + 0.75rem);
  right: calc(var(--drawer-width, 0px) + 1.25rem);
  z-index: var(--z-notification, 70);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 22rem;
}
/* In the Inventory workspace the header has a second row (its pages): notes start under it. */
.right-bar-wrapper.in-ws {
  top: calc(var(--topbar-height) + 48px + 0.75rem);
}
.flex {
  display: flex;
  width: 100%;
  overflow-x: hidden;
}
.content {
  padding: 1.5rem 1.5rem 1.5rem;
  padding-left: calc(var(--sidebar-width, 260px) + 1.5rem);
  padding-right: calc(var(--drawer-width, 0px) + 1.5rem);
  margin-top: var(--topbar-height);
  width: 100vw;
  height: calc(100vh - var(--topbar-height) - 38px);
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: padding var(--motion-base) var(--ease-out);
}
@media (max-width: 900px) {
  .content { padding: 1rem 0.75rem 1.25rem; padding-left: 0.75rem; padding-right: 0.75rem; }
  .right-bar-wrapper { right: 0.5rem; left: 0.5rem; max-width: none; }
}
.brand-watermark {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-35%, -50%);
  width: 55vw;
  max-width: 700px;
  pointer-events: none;
  z-index: 0;
}
svg.brand-watermark {
  color: var(--brand-watermark);
}
img.brand-watermark {
  opacity: var(--brand-watermark-opacity);
}
</style>
